import { useRef, useState } from 'react';
import { HttpError } from '../../../services/apiClient';
import { ApiErrorCode } from '../../../services/apiErrorCodes';
import { useCustomEmailTemplateStore } from '../../../stores/customEmailTemplateStore';
import { contentFor, TEMPLATE_LANGUAGES } from '../../../types/customEmailTemplate';
import type { CustomEmailTemplateSetting, TemplateLanguage } from '../../../types/customEmailTemplate';
import { displayMarker, insertAtCursor, toDisplay } from './customTemplateMarkers';
import type { Macro } from './customTemplateMarkers';
import { validateDrafts } from './customTemplateValidation';
import type { DraftsByLanguage, ValidationResult } from './customTemplateValidation';

export type EditorField = 'subject' | 'body';
export type SaveError = 'invalid' | 'failed';

export const FIELD_IDS: Record<EditorField, string> = {
    subject: 'custom-email-template-subject',
    body: 'custom-email-template-body',
};

const initialDrafts = (initial: CustomEmailTemplateSetting | null): DraftsByLanguage => {
    const toDraft = (language: TemplateLanguage) => {
        const content = contentFor(initial, language);
        return { subject: toDisplay(content?.subject ?? '', language), body: toDisplay(content?.body ?? '', language) };
    };
    return { FR: toDraft('FR'), EN: toDraft('EN') };
};

export const useCustomEmailTemplateEditor = (
    initial: CustomEmailTemplateSetting | null,
    initialLanguage: TemplateLanguage,
    onSaved: (saved: CustomEmailTemplateSetting) => void,
) => {
    const save = useCustomEmailTemplateStore(s => s.save);
    const [language, setLanguage] = useState<TemplateLanguage>(initialLanguage);
    const [drafts, setDrafts] = useState<DraftsByLanguage>(() => initialDrafts(initial));
    const [validation, setValidation] = useState<ValidationResult | null>(null);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState<SaveError | null>(null);
    const lastFieldRef = useRef<EditorField>('body');

    const setField = (field: EditorField, value: string) =>
        setDrafts(prev => ({ ...prev, [language]: { ...prev[language], [field]: value } }));

    const insertMacro = (macro: Macro) => {
        const field = lastFieldRef.current;
        const element = document.getElementById(FIELD_IDS[field]) as HTMLInputElement | HTMLTextAreaElement | null;
        const current = drafts[language][field];
        const start = element?.selectionStart ?? current.length;
        const end = element?.selectionEnd ?? start;
        const { value, cursor } = insertAtCursor(current, start, end, displayMarker(macro, language));
        setField(field, value);
        requestAnimationFrame(() => {
            element?.focus();
            element?.setSelectionRange(cursor, cursor);
        });
    };

    const submit = async () => {
        const result = validateDrafts(drafts);
        setValidation(result);
        setSaveError(null);
        if (!result.setting) {
            const firstInvalid = TEMPLATE_LANGUAGES.find(l => result.errors[l]);
            if (firstInvalid) setLanguage(firstInvalid);
            return;
        }
        setSaving(true);
        try {
            await save(result.setting);
            onSaved(result.setting);
        } catch (e) {
            setSaveError(e instanceof HttpError && e.code === ApiErrorCode.CustomEmailTemplateInvalid ? 'invalid' : 'failed');
        } finally {
            setSaving(false);
        }
    };

    return {
        language,
        setLanguage,
        draft: drafts[language],
        errors: validation?.errors[language] ?? {},
        noLanguage: validation?.noLanguage ?? false,
        setField,
        focusField: (field: EditorField) => { lastFieldRef.current = field; },
        insertMacro,
        saving,
        saveError,
        submit,
    };
};
